import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  getDashboardErrorMessage,
  useListDeletedDashboardTemplatesQuery,
  useRestoreDeletedDashboardTemplateMutation
} from './dashboardApi';
import {
  getDashboardDeletedRestoreConflicts,
  getDashboardTemplateStatusLabel
} from './dashboardTemplateModel';

const NOOP = () => {};

const formatDeletedAt = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Deletion date unavailable';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const assignmentLabel = (count) =>
  `${count} former ${count === 1 ? 'assignment' : 'assignments'}`;

const conflictScopeLabel = (scopeType) =>
  scopeType === 'industry_type' ? 'Industry type' : 'Entity';

const DeletedTemplateCard = ({ onRestore, template }) => (
  <Paper
    data-testid={`deleted-dashboard-${template.dashboardTemplateId}`}
    elevation={0}
    sx={{ border: '1px solid', borderColor: 'divider', p: 2.5 }}
  >
    <Stack
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      spacing={2}
    >
      <Box>
        <Typography component="h3" fontSize={18} fontWeight={700}>
          {template.name}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
          {template.description || 'No description provided.'}
        </Typography>
      </Box>
      <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1}>
        <Chip
          label={`Previously ${getDashboardTemplateStatusLabel(template).toLowerCase()}`}
          size="small"
        />
        <Chip
          label={assignmentLabel(template.deletedAssignmentCount ?? 0)}
          size="small"
          variant="outlined"
        />
      </Stack>
    </Stack>

    <Divider sx={{ my: 2 }} />
    <Typography fontWeight={700} variant="body2">
      Reason for deletion
    </Typography>
    <Typography sx={{ mt: 0.5 }} variant="body2">
      {template.deletionReason}
    </Typography>
    <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
      Deleted {formatDeletedAt(template.deletedAt)} · Audit user{' '}
      {template.deletedBy}
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
      <Button onClick={() => onRestore(template)} variant="outlined">
        Restore dashboard
      </Button>
    </Box>
  </Paper>
);

const DashboardDeletedTemplates = ({ onSuccess = NOOP }) => {
  const [open, setOpen] = useState(false);
  const [restoreCandidate, setRestoreCandidate] = useState(null);
  const deletedQuery = useListDeletedDashboardTemplatesQuery(undefined, {
    skip: !open
  });
  const [restoreTemplate, restoreState] =
    useRestoreDeletedDashboardTemplateMutation();
  const templates = deletedQuery.data?.templates ?? [];
  const conflicts = getDashboardDeletedRestoreConflicts(restoreState.error);

  const closeRestore = () => {
    if (restoreState.isLoading) return;
    setRestoreCandidate(null);
    restoreState.reset?.();
  };

  const closeDeletedView = () => {
    if (restoreState.isLoading) return;
    setOpen(false);
    setRestoreCandidate(null);
    restoreState.reset?.();
  };

  const openRestore = (template) => {
    restoreState.reset?.();
    setRestoreCandidate(template);
  };

  const submitRestore = async () => {
    if (!restoreCandidate) return;

    try {
      await restoreTemplate(restoreCandidate.dashboardTemplateId).unwrap();
      const restoredName = restoreCandidate.name;
      setRestoreCandidate(null);
      onSuccess(
        `${restoredName} restored with its previous configuration and assignments.`
      );
    } catch (_error) {
      // RTK Query exposes the normalized error through restoreState below.
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outlined">
        Deleted dashboards
      </Button>

      <Dialog fullWidth maxWidth="md" onClose={closeDeletedView} open={open}>
        <DialogTitle>Deleted dashboards</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deleted dashboards are hidden from active management and cannot be
            assigned. Their configuration, history, and data are retained until
            a customer administrator restores them.
          </DialogContentText>

          {deletedQuery.isFetching && <LinearProgress sx={{ mt: 2 }} />}
          {deletedQuery.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {getDashboardErrorMessage(deletedQuery.error)}
            </Alert>
          )}
          {!deletedQuery.isFetching &&
            !deletedQuery.error &&
            !templates.length && (
              <Alert severity="info" sx={{ mt: 2 }}>
                There are no deleted dashboards.
              </Alert>
            )}
          {templates.length > 0 && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {templates.map((template) => (
                <DeletedTemplateCard
                  key={template.dashboardTemplateId}
                  onRestore={openRestore}
                  template={template}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={restoreState.isLoading} onClick={closeDeletedView}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={closeRestore}
        open={Boolean(restoreCandidate)}
      >
        <DialogTitle>Restore dashboard?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {restoreCandidate?.name} will return to active dashboard management
            with its previous configuration and former assignments. The restore
            will only complete if every former assignment is still available.
          </DialogContentText>
          {restoreState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {getDashboardErrorMessage(restoreState.error)}
              {conflicts.length > 0 && (
                <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2.5 }}>
                  {conflicts.map((conflict) => (
                    <li key={`${conflict.scopeType}-${conflict.scopeId}`}>
                      {conflictScopeLabel(conflict.scopeType)} ·{' '}
                      {conflict.scopeId}
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={restoreState.isLoading} onClick={closeRestore}>
            Cancel
          </Button>
          <Button
            disabled={restoreState.isLoading}
            onClick={submitRestore}
            variant="contained"
          >
            {restoreState.isLoading ? 'Restoring…' : 'Restore dashboard'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DashboardDeletedTemplates;
